import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from 'app/components/header/header.component';
import { FooterComponent } from 'app/components/footer/footer.component';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApplicationService } from 'app/services/application.service';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { formatUrlEncode, htmlToString } from 'app/shared/utils';

@Component({
    selector: 'app-campaign',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    imports: [CommonModule, HeaderComponent, FooterComponent, TranslateModule],
    templateUrl: './campaign.component.html',
    styleUrl: './campaign.component.scss',
})
export class CampaignComponent implements OnInit {
    router = inject(Router);
    applicationService = inject(ApplicationService);
    selectedLanguage: any;
    isAuthenticated: boolean = false;
    tcPath = {
        bm: '/assets/documents/FS-CGC-Campaign-BM.pdf',
        en: '/assets/documents/FS-CGC-Campaign-EN.pdf',
    };

    dataBM: any;
    dataEN: any;
    campaignId: any;
    campaignData: any;
    campaignStartDate: any;
    campaignEndDate: any;
    campSlug: any;

    constructor(
        private toastr: ToastrService,
        private activatedRoute: ActivatedRoute
    ) {
        this.isAuthenticated =
            localStorage.getItem('isAuthenticated') == 'true';
    }

    ngOnInit(): void {
        this.selectedLanguage = this.readLocalStorageValue('language');
        this.activatedRoute.params.subscribe((params) => {
            this.campaignId = params?.id;
            this.campSlug = params?.slug;
            if (this.campaignId) {
                this.getCampaignById(this.campaignId);
            }
        });
    }

    getCampaignById(id: any, urlSlug?: string) {
        this.applicationService.getCampaignDetailsById(id).subscribe({
            next: (data) => {
                let res = data;
                /* TO SEPARATE EN AND BM DATA SO THAT WHEN CHANGES HAPPENS IT WONT DISPLAY WRONG DATA */
                this.dataBM = [];
                this.dataEN = [];
                let newObj = {};
                if (res?.campaigndetails && res?.campaigndetails?.length) {
                    if (res?.campaigndetails[0]?.languages == 'BM') {
                        newObj = { ...res };
                        newObj['campaigndetails'] = res?.campaigndetails[0];
                        this.dataBM = newObj;
                    } else if (res?.campaigndetails[0]?.languages == 'EN') {
                        newObj = { ...res };
                        newObj['campaigndetails'] = res?.campaigndetails[0];
                        this.dataEN = newObj;
                    }

                    if (res?.campaigndetails[1]?.languages == 'BM') {
                        newObj = { ...res };
                        newObj['campaigndetails'] = res?.campaigndetails[1];
                        this.dataBM = newObj;
                    } else if (res?.campaigndetails[1]?.languages == 'EN') {
                        newObj = { ...res };
                        newObj['campaigndetails'] = res?.campaigndetails[1];
                        this.dataEN = newObj;
                    }
                }

                const enSlug = formatUrlEncode(
                    htmlToString(
                        this.dataEN?.campaigndetails?.campaignDescription
                    )
                );
                const bmSlug = formatUrlEncode(
                    htmlToString(
                        this.dataBM?.campaigndetails?.campaignDescription
                    )
                );

                if (this.campSlug === enSlug) {
                    this.switchedLanguage('en');
                } else if (this.campSlug === bmSlug) {
                    this.switchedLanguage('bm');
                }

                if (this.selectedLanguage == 'bm') {
                    this.campaignData = this.dataBM;
                    const startDate = this.campaignData?.campaignStartDate;
                    const endDate = this.campaignData?.campaignEndDate;
                    this.campaignStartDate = this.getDateWithFormat(
                        startDate,
                        'ms-MY'
                    );
                    this.campaignEndDate = this.getDateWithFormat(
                        endDate,
                        'ms-MY'
                    );
                } else {
                    this.campaignData = this.dataEN;
                    const startDate = this.campaignData?.campaignStartDate;
                    const endDate = this.campaignData?.campaignEndDate;
                    this.campaignStartDate = this.getDateWithFormat(
                        startDate,
                        'en-US'
                    );
                    this.campaignEndDate = this.getDateWithFormat(
                        endDate,
                        'en-US'
                    );
                }
            },
            error: (err) => {},
        });
    }

    getDateWithFormat(data, locale) {
        return moment.utc(data).locale(locale).format('MMMM DD, YYYY');
    }

    readLocalStorageValue(key: string) {
        let value = localStorage.getItem(key);
        return value;
    }

    switchedLanguage(data: any) {
        this.selectedLanguage = data;

        if (this.selectedLanguage == 'bm') {
            this.campaignData = this.dataBM;
            const startDate = this.campaignData?.campaignStartDate;
            const endDate = this.campaignData?.campaignEndDate;
            this.campaignStartDate = this.getDateWithFormat(startDate, 'ms-MY');
            this.campaignEndDate = this.getDateWithFormat(endDate, 'ms-MY');
            this.redirectToCampaignUrl('bm'); // Redirect to BM campaign URL
        } else {
            this.campaignData = this.dataEN;
            const startDate = this.campaignData?.campaignStartDate;
            const endDate = this.campaignData?.campaignEndDate;
            this.campaignStartDate = this.getDateWithFormat(startDate, 'en-US');
            this.campaignEndDate = this.getDateWithFormat(endDate, 'en-US');
            this.redirectToCampaignUrl('en'); // Redirect to EN campaign URL
        }
    }

    /**
     * Detects language based on URL slug by comparing with campaign descriptions
     * @param urlSlug - The slug from the URL
     * @returns 'bm' | 'en' | null
     */
    private detectLanguageFromUrlSlug(urlSlug: string): string | null {
        if (!urlSlug || (!this.dataBM && !this.dataEN)) {
            return null;
        }

        // Generate slugs for both languages
        const bmSlug = this.dataBM?.campaigndetails?.campaignDescription
            ? formatUrlEncode(
                  htmlToString(this.dataBM.campaigndetails.campaignDescription)
              )
            : null;

        const enSlug = this.dataEN?.campaigndetails?.campaignDescription
            ? formatUrlEncode(
                  htmlToString(this.dataEN.campaigndetails.campaignDescription)
              )
            : null;

        // Handle edge case where both descriptions are the same
        if (bmSlug && enSlug && bmSlug === enSlug) {
            // If descriptions are identical, check URL patterns or fallback logic
            return this.handleIdenticalDescriptions(urlSlug);
        }

        // Check exact matches first
        if (bmSlug && urlSlug === bmSlug) {
            return 'bm';
        }

        if (enSlug && urlSlug === enSlug) {
            return 'en';
        }

        // If no exact match, try partial matching (in case of URL encoding differences)
        if (bmSlug && this.isSlugMatch(urlSlug, bmSlug)) {
            return 'bm';
        }

        if (enSlug && this.isSlugMatch(urlSlug, enSlug)) {
            return 'en';
        }

        return null; // No match found, keep current language
    }

    /**
     * Handles the case where BM and EN descriptions are identical
     * @param urlSlug - The slug from the URL
     * @returns 'bm' | 'en' | null
     */
    private handleIdenticalDescriptions(urlSlug: string): string | null {
        // Strategy 1: Check for language indicators in the URL slug
        const languageIndicators = {
            bm: ['bm', 'malay', 'malaysia', 'melayu'],
            en: ['en', 'english', 'eng'],
        };

        const lowerSlug = urlSlug.toLowerCase();

        // Check for BM indicators
        for (const indicator of languageIndicators.bm) {
            if (lowerSlug.includes(indicator)) {
                return 'bm';
            }
        }

        // Check for EN indicators
        for (const indicator of languageIndicators.en) {
            if (lowerSlug.includes(indicator)) {
                return 'en';
            }
        }

        // Strategy 2: Check current browser language or previous selection
        const browserLang = navigator.language.toLowerCase();
        if (browserLang.includes('ms') || browserLang.includes('my')) {
            return 'bm';
        }

        // Strategy 3: Fallback to current selected language or default to 'en'
        return this.selectedLanguage || 'en';
    }

    /**
     * Checks if two slugs match with some tolerance for encoding differences
     * @param urlSlug - Slug from URL
     * @param generatedSlug - Generated slug from description
     * @returns boolean
     */
    private isSlugMatch(urlSlug: string, generatedSlug: string): boolean {
        // Normalize both slugs for comparison
        const normalizeSlug = (slug: string) => {
            return slug
                .toLowerCase()
                .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens
                .replace(/\s+/g, '-') // Replace spaces with hyphens
                .replace(/-+/g, '-') // Replace multiple hyphens with single
                .trim();
        };

        const normalizedUrl = normalizeSlug(urlSlug);
        const normalizedGenerated = normalizeSlug(generatedSlug);

        // Check exact match after normalization
        if (normalizedUrl === normalizedGenerated) {
            return true;
        }

        // Check if one contains the other (for partial matches)
        const similarity = this.calculateSimilarity(
            normalizedUrl,
            normalizedGenerated
        );
        return similarity > 0.8; // 80% similarity threshold
    }

    /**
     * Calculates similarity between two strings using Levenshtein distance
     * @param str1 - First string
     * @param str2 - Second string
     * @returns number between 0 and 1
     */
    private calculateSimilarity(str1: string, str2: string): number {
        const longer = str1.length > str2.length ? str1 : str2;
        const shorter = str1.length > str2.length ? str2 : str1;

        if (longer.length === 0) {
            return 1.0;
        }

        const distance = this.levenshteinDistance(longer, shorter);
        return (longer.length - distance) / longer.length;
    }

    /**
     * Calculates Levenshtein distance between two strings
     * @param str1 - First string
     * @param str2 - Second string
     * @returns number
     */
    private levenshteinDistance(str1: string, str2: string): number {
        const matrix = [];

        for (let i = 0; i <= str2.length; i++) {
            matrix[i] = [i];
        }

        for (let j = 0; j <= str1.length; j++) {
            matrix[0][j] = j;
        }

        for (let i = 1; i <= str2.length; i++) {
            for (let j = 1; j <= str1.length; j++) {
                if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1,
                        matrix[i][j - 1] + 1,
                        matrix[i - 1][j] + 1
                    );
                }
            }
        }

        return matrix[str2.length][str1.length];
    }

    /**
     * Redirects to campaign URL with language-specific slug
     * @param language - The selected language ('bm' or 'en')
     * @returns void
     */
    private redirectToCampaignUrl(language: string): void {
        if (!this.campaignId) return;

        const campaignData = language === 'bm' ? this.dataBM : this.dataEN;
        const campaignDescription =
            campaignData?.campaigndetails?.campaignDescription;

        if (campaignDescription) {
            const slug = formatUrlEncode(htmlToString(campaignDescription));
            const newUrl = `/campaign/${this.campaignId}/${slug}`;

            // Only navigate if the current URL is different to avoid unnecessary navigation
            if (this.router.url !== newUrl) {
                this.router.navigateByUrl(newUrl);
            }
        }
    }

    openProductPage(
        prodCode: string,
        partner: string,
        source: string,
        type: any
    ) {
        let whitespace = new Set([' ', '\t', '\n']);
        if (
            prodCode.includes('https://') ||
            prodCode.includes('http://') ||
            prodCode.includes('www') ||
            prodCode.includes('/')
        ) {
            if (type == 'External') {
                window.open(prodCode, '_blank');
            }
            if (type == 'Internal') {
                window.open(prodCode, '_self');
            }
        } else {
            for (let i = 0; i < prodCode.length; i++) {
                if (whitespace.has(prodCode[i])) {
                    if (this.selectedLanguage == 'bm') {
                        this.toastr.error('Oops. Terdapat ralat dalam sistem.');
                    } else {
                        this.toastr.error('Oops.Something went wrong.');
                    }
                } else {
                    let productCode = prodCode;
                    if (this.isAuthenticated) {
                        this.router.navigate(['/business-financing'], {
                            queryParams: {
                                productCode: productCode,
                                source: source,
                                campaignPartner: partner,
                            },
                        });
                    } else {
                        this.router.navigate(['/login'], {
                            queryParams: {
                                returnUrl: `/business-financing?productCode=${productCode}&source=${source}&campaignPartner=${partner}`,
                            },
                        });
                    }
                }
            }
        }
    }
}
